import type { CreditView, Refund } from '../../components/refund-api';
import { api } from '../../lib/api-client';

export type BookingStatus = 'OPEN' | 'CONFIRMED' | 'CANCELLED' | 'CONVERTED';
export type BookingCancelledBy = 'CUSTOMER' | 'DEALER';
export type DepositOutcome = 'HELD' | 'FORFEITED' | 'REFUNDED';

/** Money is a decimal STRING on this boundary — see money.ts for why. */
export interface Booking {
  uid: string;
  bookingNo: string;
  status: BookingStatus;
  customerUid: string;
  customerName: string;
  quotationNo: string | null;
  /** The AutoCount item code of the variant quoted: what the deposit should reserve. */
  quotedItemCode: string | null;
  quotedBike: string | null;
  unitUid: string | null;
  chassisNo: string | null;
  model: string | null;
  depositAmount: string;
  depositRef: string | null;
  depositOutcome: DepositOutcome;
  currency: string;
  validUntil: string | null;
  hasLapsed: boolean;
  confirmedAt: string | null;
  expectedDeliveryAt: string | null;
  cancelledBy: BookingCancelledBy | null;
  cancelReason: string | null;
  branchCode: string | null;
  createdAt: string;
  /** A bike and deposit chosen and on the cashier's Till: OPEN, or a booking whose bike was taken. Nothing is held yet. */
  awaitingDeposit: boolean;
  /** CONFIRMED with no bike: a manager's override gave it to another booking. Its money stays on it. */
  bikeTaken: boolean;
  pendingUnitUid: string | null;
  pendingChassisNo: string | null;
  pendingDepositAmount: string | null;
  /** The manager who authorised a stored override, by name. */
  pendingOverrideBy: string | null;
  /** The booking the bike is to be taken from, when an allocation override is stored. */
  pendingOverrideHolderBookingNo: string | null;
}

export interface BookingPage {
  items: Booking[];
  nextCursor: string | null;
  hasMore: boolean;
}

export function listBookings(params: {
  q?: string;
  status?: BookingStatus;
  lapsed?: boolean;
  cursor?: string;
}): Promise<BookingPage> {
  const search = new URLSearchParams();
  if (params.q !== undefined && params.q.length > 0) search.set('q', params.q);
  if (params.status !== undefined) search.set('status', params.status);
  if (params.lapsed === true) search.set('lapsed', 'true');
  if (params.cursor !== undefined && params.cursor.length > 0) search.set('cursor', params.cursor);
  const qs = search.toString();
  return api<BookingPage>(`/bookings${qs.length > 0 ? `?${qs}` : ''}`);
}

/** A motorcycle that can still be reserved, as the deposit drawer needs it. */
export interface AvailableUnit {
  uid: string;
  chassisNo: string;
  model: string | null;
  colour: string | null;
  autocountItemCode: string | null;
}

export interface AvailableUnitPage {
  items: AvailableUnit[];
  nextCursor: string | null;
  hasMore: boolean;
}

/**
 * Unsold motorcycles, filtered on the server: by the quoted variant's item
 * code, or by a chassis/engine/plate search. Never the first page of
 * everything — the quoted chassis is rarely in it.
 */
export function listAvailableUnits(params: { itemCode?: string; q?: string }): Promise<AvailableUnitPage> {
  const search = new URLSearchParams({ available: 'true' });
  if (params.itemCode !== undefined && params.itemCode.length > 0) search.set('itemCode', params.itemCode);
  if (params.q !== undefined && params.q.length > 0) search.set('q', params.q);
  return api<AvailableUnitPage>(`/units?${search.toString()}`);
}

export function getBooking(uid: string): Promise<Booking> {
  return api<Booking>(`/bookings/${uid}`);
}

export function createBooking(input: {
  quotationUid: string;
  expectedDeliveryAt?: string;
}): Promise<Booking> {
  return api<Booking>('/bookings', { method: 'POST', body: input });
}

/**
 * Chooses the bike and deposit and puts the booking on the cashier's Till. The
 * bike is NOT held by this: the cashier taking the deposit is what reserves it.
 */
export function sendToCashier(
  uid: string,
  input: {
    unitUid: string;
    depositAmount: string;
    /** A manager's reason to take a bike reserved for another booking. */
    overrideReason?: string;
    /** A manager's reason to reserve a different variant from the one quoted. */
    variantOverrideReason?: string;
  },
): Promise<Booking> {
  return api<Booking>(`/bookings/${uid}/pending-deposit`, { method: 'PUT', body: input });
}

/** Takes the booking off the Till: clears the chosen bike, deposit and any stored override. */
export function clearPending(uid: string): Promise<Booking> {
  return api<Booking>(`/bookings/${uid}/pending-deposit`, { method: 'DELETE' });
}

export function cancelBooking(
  uid: string,
  input: { cancelledBy: BookingCancelledBy; reason: string },
): Promise<Booking> {
  return api<Booking>(`/bookings/${uid}/transitions/cancel`, { method: 'POST', body: input });
}

/** The booking's receipts, the credit it holds, and its invoice once billed. */
export interface BookingMoney {
  receipts: {
    uid: string;
    receiptNo: string;
    method: string;
    amount: string;
    reference: string | null;
    receivedAt: string;
    invoiceNo: string | null;
    /** A refund paid out: the amount is negative. */
    isRefund: boolean;
    /** The receipt can be printed: a deposit or down payment, not a refund payout. */
    printable: boolean;
  }[];
  creditHeld: string;
  /** Held, forfeited, refunded, awaiting payout and refundable. */
  credit: CreditView;
  refunds: Refund[];
  /** The caller may approve a refund and there is credit to refund. */
  canRefund: boolean;
  invoice: { uid: string; invoiceNo: string; status: string; grandTotal: string; balanceDue: string } | null;
}

export function getBookingMoney(uid: string): Promise<BookingMoney> {
  return api<BookingMoney>(`/pos/bookings/${uid}/money`);
}

